// Designer editor for a handler='open' action button — "open ANOTHER object
// with values from this record".
//
// A transaction target: how it opens (add / edit / edit-or-add, and which
// columns find the record), the columns to set in order (each one firing its
// item_change, optionally locked), and the follow-up steps (a Line Entry
// command that pulls the detail lines, or an action button of the target).
// A process / dashboard / visual / report target: its criteria / filters /
// prompts; a wizard target: its declared launch inputs. Values are templates — {form.COLUMN}, {header.COLUMN} or a literal.
//
// The target's columns, line commands and action buttons are read from its
// model to offer as suggestions; every field still accepts a typed name, so
// an object that does not exist yet can be wired up first.

import { useEffect, useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import type {
  ActionDef, OpenActionBinding, OpenActionMode, OpenActionSetEntry, OpenActionThenStep,
  OpenObjectType, SqlModel, TransactionModel,
} from './types'
import { OPEN_MODE_LABELS, OPEN_OBJ_TYPE_LABELS, actionName } from './types'
import { columnFormKey } from '@twasta/page-runtime/txn'
import { lineEntryOf } from '@twasta/page-runtime/txn/lineEntry'
import { listDeployableObjects } from '@/api/deploy'
import { loadTransactionObject } from '@/api/designers'
import { listWizards } from '@/api/wizards'
import ObjectPickerField from '../page/ObjectPickerField'
import { useWizardInputs } from './WizardActionEditor'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: '0.9375rem', minHeight: 36, boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontWeight: 600 }
const hintStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }
const iconBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 2,
  color: 'var(--color-text-secondary)', display: 'inline-flex', alignItems: 'center',
}
const addBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)',
  fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 3,
}
const sectionHead: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 4px',
}

const OBJ_TYPES: OpenObjectType[] = ['T', 'G', 'D', 'V', 'R', 'U']
const MODES: OpenActionMode[] = ['add', 'edit', 'edit_or_add', 'view']
const PARAM_NOUN: Record<OpenObjectType, string> = {
  T: '', G: 'criterion', D: 'filter (global criterion)', V: 'prompt', R: 'report prompt',
  U: 'wizard input',
}

interface TargetInfo {
  columns: Array<{ key: string; label: string }>
  lineForms: Array<{ formNo: string; title: string; commands: string[] }>
  actions: Array<{ formNo: string; name: string }>
}

function targetInfoOf(model: TransactionModel | null): TargetInfo {
  const out: TargetInfo = { columns: [], lineForms: [], actions: [] }
  const forms = (model?.sql_models || []).map(w => w?.sql_model).filter(Boolean) as SqlModel[]
  const header = forms[0]
  for (const w of header?.columns || []) {
    const c = w?.column
    if (!c) continue
    out.columns.push({ key: c.db_name || columnFormKey(c), label: String(c.heading || c.name || columnFormKey(c)) })
  }
  forms.forEach((sm, i) => {
    const formNo = String(sm.form_no || i + 1)
    for (const a of sm.actions || []) {
      const name = actionName(a)
      if (name) out.actions.push({ formNo, name })
    }
    if (i === 0) return
    const le = lineEntryOf(sm)
    if (le?.commands.length) {
      out.lineForms.push({ formNo, title: String(sm.form_title || `Form ${formNo}`), commands: le.commands.map(c => c.name) })
    }
  })
  return out
}

/** Edit an ordered key → value map as rows (keys may repeat while typing). */
function MapRows({
  map, onChange, keyPlaceholder, valuePlaceholder, keyList, valueList, protectable,
}: {
  map: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
  keyPlaceholder: string
  valuePlaceholder: string
  keyList?: string
  valueList?: string
  /** Offer a "lock" checkbox per row ({value, protect} entries). */
  protectable?: boolean
}) {
  const entries = Object.entries(map || {})
  const valueOf = (v: unknown) => (v && typeof v === 'object' && 'value' in (v as object) ? (v as { value: unknown }).value : v)
  const lockedOf = (v: unknown) => !!(v && typeof v === 'object' && (v as { protect?: boolean }).protect)
  const write = (next: Array<[string, unknown]>) => onChange(Object.fromEntries(next))
  return (
    <>
      {entries.map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
          <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={k} placeholder={keyPlaceholder} list={keyList}
            onChange={e => { const n = [...entries]; n[i] = [e.target.value, v]; write(n) }} />
          <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={String(valueOf(v) ?? '')} placeholder={valuePlaceholder}
            list={valueList}
            onChange={e => {
              const n = [...entries]
              n[i] = [k, lockedOf(v) ? { value: e.target.value, protect: true } : e.target.value]
              write(n)
            }} />
          {protectable && (
            <label title="Show this value locked on the opened screen" style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 2 }}>
              <input type="checkbox" checked={lockedOf(v)}
                onChange={e => {
                  const n = [...entries]
                  n[i] = [k, e.target.checked ? { value: valueOf(v), protect: true } : valueOf(v)]
                  write(n)
                }} />
              Lock
            </label>
          )}
          <button style={{ ...iconBtn, color: 'var(--color-error, #cf222e)' }} title="Remove"
            onClick={() => write(entries.filter((_, xi) => xi !== i))}>
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </>
  )
}

export default function OpenActionEditor({
  def, onPatch, project, formColumns, headerColumns, actionIndex,
}: {
  def: ActionDef
  onPatch: (patch: Partial<ActionDef>) => void
  project: string
  /** This form's / its header's columns — offered as {form.X} / {header.X}. */
  formColumns: string[]
  headerColumns: string[]
  /** Distinguishes this card's suggestion lists from its neighbours'. */
  actionIndex: number
}) {
  const b: OpenActionBinding = def.open || { obj_name: '' }
  const patch = (p: Partial<OpenActionBinding>) => onPatch({ open: { ...b, ...p } })
  const objType: OpenObjectType = OBJ_TYPES.includes(b.obj_type as OpenObjectType) ? b.obj_type as OpenObjectType : 'T'
  const mode: OpenActionMode = MODES.includes(b.mode as OpenActionMode) ? b.mode as OpenActionMode : 'add'
  const objName = (b.obj_name || '').trim()

  // Objects of the project, for the picker.
  const [objects, setObjects] = useState<Array<{ obj_name: string; obj_type: string }>>([])
  useEffect(() => {
    if (!project) return undefined
    let cancelled = false
    listDeployableObjects(project)
      .then(list => { if (!cancelled) setObjects(list.map(o => ({ obj_name: o.obj_name, obj_type: o.obj_type }))) })
      .catch(err => { console.warn('[OpenActionEditor] could not list project objects', err) })
    return () => { cancelled = true }
  }, [project])
  // Wizards are listed from their own folder (a wizard not yet on the menu is
  // still a valid target), merged with any the object listing carries.
  const [wizards, setWizards] = useState<Array<{ obj_name: string; title?: string; base_object?: string }>>([])
  useEffect(() => {
    if (!project || objType !== 'U') return undefined
    let cancelled = false
    listWizards(project)
      .then(list => { if (!cancelled) setWizards(list) })
      .catch(err => { console.warn('[OpenActionEditor] could not list wizards', err) })
    return () => { cancelled = true }
  }, [project, objType])
  const catalog = useMemo(() => {
    const own = objects.filter(o => o.obj_type === objType).map(o => ({ obj_name: o.obj_name, hint: OPEN_OBJ_TYPE_LABELS[objType] }))
    if (objType !== 'U') return own
    const seen = new Set(own.map(o => o.obj_name.toLowerCase()))
    return [
      ...own,
      ...wizards.filter(w => !seen.has(w.obj_name.toLowerCase())).map(w => ({
        obj_name: w.obj_name, title: w.title,
        hint: w.base_object ? `Wizard on ${w.base_object}` : OPEN_OBJ_TYPE_LABELS.U,
      })),
    ]
  }, [objects, objType, wizards])
  // A wizard target's declared inputs — the names its params may take.
  const wizardInputs = useWizardInputs(project, objType === 'U' ? objName : '')
  const inputList = `open-inputs-${actionIndex}`

  // The target transaction's model, for column / command / action suggestions.
  const [target, setTarget] = useState<TargetInfo>(() => targetInfoOf(null))
  useEffect(() => {
    if (objType !== 'T' || !objName || !project) { setTarget(targetInfoOf(null)); return undefined }
    let cancelled = false
    const t = setTimeout(() => {
      loadTransactionObject(project, objName)
        .then((data) => {
          if (cancelled) return
          const root = data?.content as { transaction?: TransactionModel } | TransactionModel | null
          const model = root && 'transaction' in (root as object)
            ? (root as { transaction?: TransactionModel }).transaction || null
            : (root as TransactionModel | null)
          setTarget(targetInfoOf(model))
        })
        .catch(() => { if (!cancelled) setTarget(targetInfoOf(null)) })
    }, 400)
    return () => { cancelled = true; clearTimeout(t) }
  }, [project, objName, objType])

  const tokenList = `open-tokens-${actionIndex}`
  const colList = `open-cols-${actionIndex}`
  const tokens = useMemo(() => Array.from(new Set([
    ...formColumns.map(c => `{form.${c}}`),
    ...headerColumns.map(c => `{header.${c}}`),
  ])), [formColumns, headerColumns])

  const sets: OpenActionSetEntry[] = b.set || []
  const writeSets = (next: OpenActionSetEntry[]) => patch({ set: next })
  const then: OpenActionThenStep[] = b.then || []
  const writeThen = (next: OpenActionThenStep[]) => patch({ then: next })
  const move = <T,>(list: T[], from: number, to: number): T[] => {
    if (to < 0 || to >= list.length) return list
    const next = [...list]
    const [x] = next.splice(from, 1)
    next.splice(to, 0, x)
    return next
  }

  return (
    <div style={{ marginBottom: 6 }}>
      <datalist id={tokenList}>{tokens.map(t => <option key={t} value={t} />)}</datalist>
      <datalist id={colList}>{target.columns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</datalist>

      <label style={labelStyle}>Open a</label>
      <select style={{ ...inputStyle, marginBottom: 6 }} value={objType}
        onChange={e => patch({ obj_type: e.target.value as OpenObjectType })}>
        {OBJ_TYPES.map(t => <option key={t} value={t}>{OPEN_OBJ_TYPE_LABELS[t]}</option>)}
      </select>

      <div style={{ marginBottom: 6 }}>
        <ObjectPickerField
          label={OPEN_OBJ_TYPE_LABELS[objType]}
          value={b.obj_name || ''}
          onChange={(name) => patch({ obj_name: name })}
          catalog={catalog}
          typeLabel={OPEN_OBJ_TYPE_LABELS[objType].toLowerCase()}
          placeholder={`Pick the ${OPEN_OBJ_TYPE_LABELS[objType].toLowerCase()} to open…`}
          emptyHint="None in this project yet — type its name to wire it up first."
        />
      </div>

      {objType === 'T' ? (
        <>
          <label style={labelStyle}>How it opens</label>
          <select style={{ ...inputStyle, marginBottom: 4 }} value={mode}
            onChange={e => patch({ mode: e.target.value as OpenActionMode })}>
            {MODES.map(m => <option key={m} value={m}>{OPEN_MODE_LABELS[m]}</option>)}
          </select>
          {mode !== 'add' && (
            <>
              <div style={sectionHead}>
                <span style={labelStyle}>Find the record by (its column = value)</span>
                <button style={addBtn} onClick={() => {
                  const m = { ...(b.match || {}) }
                  let n = Object.keys(m).length + 1
                  while (m[`COLUMN_${n}`] !== undefined) n += 1
                  m[`COLUMN_${n}`] = ''
                  patch({ match: m })
                }}><Plus size={11} /> Add</button>
              </div>
              <MapRows map={b.match || {}} onChange={m => patch({ match: m })}
                keyPlaceholder="its column" valuePlaceholder="{form.COLUMN} or literal"
                keyList={colList} valueList={tokenList} />
              <div style={hintStyle}>
                One match opens it {mode === 'view' ? 'read-only' : 'for editing'}{mode === 'edit_or_add' ? ', none adds a new one' : ''};
                several open its list filtered to them so the user picks.
              </div>
            </>
          )}

          <div style={sectionHead}>
            <span style={labelStyle}>Set these columns, in order</span>
            <button style={addBtn} onClick={() => writeSets([...sets, { column: '', value: '' }])}>
              <Plus size={11} /> Add
            </button>
          </div>
          {sets.map((entry, si) => (
            <div key={si} style={{ border: '1px solid var(--color-border)', borderRadius: 4, padding: 6, marginBottom: 4 }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
                <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={entry.column} placeholder="its column"
                  list={colList}
                  onChange={e => writeSets(sets.map((x, xi) => (xi === si ? { ...x, column: e.target.value } : x)))} />
                <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={String(entry.value ?? '')}
                  placeholder="{form.COLUMN} or literal" list={tokenList}
                  onChange={e => writeSets(sets.map((x, xi) => (xi === si ? { ...x, value: e.target.value } : x)))} />
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Run this column's item_change after setting it, and wait for it before the next column">
                  <input type="checkbox" checked={entry.item_change !== false}
                    onChange={e => writeSets(sets.map((x, xi) => (xi === si ? { ...x, item_change: e.target.checked ? undefined : false } : x)))} />
                  Run its item change
                </label>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Show the field locked after setting it">
                  <input type="checkbox" checked={!!entry.protect}
                    onChange={e => writeSets(sets.map((x, xi) => (xi === si ? { ...x, protect: e.target.checked || undefined } : x)))} />
                  Lock it
                </label>
                <span style={{ flex: 1 }} />
                <button style={iconBtn} title="Move up" onClick={() => writeSets(move(sets, si, si - 1))}><ArrowUp size={12} /></button>
                <button style={iconBtn} title="Move down" onClick={() => writeSets(move(sets, si, si + 1))}><ArrowDown size={12} /></button>
                <button style={{ ...iconBtn, color: 'var(--color-error, #cf222e)' }} title="Remove"
                  onClick={() => writeSets(sets.filter((_, xi) => xi !== si))}><Trash2 size={12} /></button>
              </div>
            </div>
          ))}

          <div style={sectionHead}>
            <span style={labelStyle}>Then</span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button style={addBtn} onClick={() => writeThen([...then, {
                run: 'line_command', form_no: target.lineForms[0]?.formNo || '',
                command: target.lineForms[0]?.commands[0] || '', select: 'all',
              }])}><Plus size={11} /> Pull lines</button>
              <button style={addBtn} onClick={() => writeThen([...then, {
                run: 'action', action_name: target.actions[0]?.name || '',
              }])}><Plus size={11} /> Run an action</button>
            </span>
          </div>
          {then.map((step, ti) => {
            const form = target.lineForms.find(f => f.formNo === step.form_no)
            const write = (p: Partial<OpenActionThenStep>) => writeThen(then.map((x, xi) => (xi === ti ? { ...x, ...p } : x)))
            return (
              <div key={ti} style={{ border: '1px solid var(--color-border)', borderRadius: 4, padding: 6, marginBottom: 4 }}>
                {step.run === 'line_command' ? (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={labelStyle}>Lines:</span>
                    <input style={{ ...inputStyle, width: 70, flex: '0 0 auto' }} value={step.form_no || ''} placeholder="form"
                      list={`open-forms-${actionIndex}-${ti}`} onChange={e => write({ form_no: e.target.value })} />
                    <datalist id={`open-forms-${actionIndex}-${ti}`}>
                      {target.lineForms.map(f => <option key={f.formNo} value={f.formNo}>{f.title}</option>)}
                    </datalist>
                    <input style={{ ...inputStyle, flex: 1, minWidth: 90 }} value={step.command || ''} placeholder="line command"
                      list={`open-cmds-${actionIndex}-${ti}`} onChange={e => write({ command: e.target.value })} />
                    <datalist id={`open-cmds-${actionIndex}-${ti}`}>
                      {(form?.commands || target.lineForms.flatMap(f => f.commands)).map(c => <option key={c} value={c} />)}
                    </datalist>
                    <select style={{ ...inputStyle, width: 'auto', flex: '0 0 auto' }} value={step.select === 'ask' ? 'ask' : 'all'}
                      onChange={e => write({ select: e.target.value as 'all' | 'ask' })}>
                      <option value="all">add every line</option>
                      <option value="ask">let the user pick</option>
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={labelStyle}>Action:</span>
                    <input style={{ ...inputStyle, flex: 1, minWidth: 0 }} value={step.action_name || ''} placeholder="its action button"
                      list={`open-acts-${actionIndex}-${ti}`} onChange={e => {
                        const hit = target.actions.find(a => a.name === e.target.value)
                        write({ action_name: e.target.value, ...(hit ? { form_no: hit.formNo } : {}) })
                      }} />
                    <datalist id={`open-acts-${actionIndex}-${ti}`}>
                      {target.actions.map(a => <option key={`${a.formNo}:${a.name}`} value={a.name}>form {a.formNo}</option>)}
                    </datalist>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 4 }}>
                  <button style={iconBtn} title="Move up" onClick={() => writeThen(move(then, ti, ti - 1))}><ArrowUp size={12} /></button>
                  <button style={iconBtn} title="Move down" onClick={() => writeThen(move(then, ti, ti + 1))}><ArrowDown size={12} /></button>
                  <button style={{ ...iconBtn, color: 'var(--color-error, #cf222e)' }} title="Remove"
                    onClick={() => writeThen(then.filter((_, xi) => xi !== ti))}><Trash2 size={12} /></button>
                </div>
              </div>
            )
          })}
          <div style={hintStyle}>
            Nothing is saved for the user — the record opens filled in, and they review and save it.
            An item change that already returns the lines needs no “Pull lines” step.
          </div>
        </>
      ) : (
        <>
          <div style={sectionHead}>
            <span style={labelStyle}>Pass these {PARAM_NOUN[objType]} values</span>
            <button style={addBtn} onClick={() => {
              const m = { ...(b.params || {}) }
              let n = Object.keys(m).length + 1
              while (m[`PARAM_${n}`] !== undefined) n += 1
              m[`PARAM_${n}`] = ''
              patch({ params: m })
            }}><Plus size={11} /> Add</button>
          </div>
          <MapRows map={(b.params || {}) as Record<string, unknown>} onChange={m => patch({ params: m })}
            keyPlaceholder={PARAM_NOUN[objType]} valuePlaceholder="{form.COLUMN} or literal"
            keyList={objType === 'U' && wizardInputs ? inputList : undefined}
            valueList={tokenList} protectable={objType === 'G'} />
          {objType === 'U' && wizardInputs && (
            <datalist id={inputList}>
              {wizardInputs.map(x => <option key={x.name} value={x.name}>{x.label || ''}</option>)}
            </datalist>
          )}
          <div style={hintStyle}>
            {objType === 'R'
              ? 'Opens the report in its viewer with these prompts filled in; the rest are asked.'
              : objType === 'U'
                ? <>The wizard starts with these launch inputs{wizardInputs?.length
                  ? <> (it declares {wizardInputs.map(x => x.name).join(', ')})</> : null}; the rest are asked or take their defaults.
                  A &ldquo;Start a wizard&rdquo; button does the same with a grid of the wizard&rsquo;s inputs.</>
                : 'Values with no match on the record are left for the user to fill.'}
          </div>
        </>
      )}

      {objType !== 'R' && (
        <>
          <label style={labelStyle}>Show it</label>
          <select style={{ ...inputStyle, marginBottom: 6 }} value={b.presentation === 'drawer' ? 'drawer' : 'navigate'}
            onChange={e => patch({ presentation: e.target.value as 'navigate' | 'drawer' })}>
            <option value="navigate">On its own screen (Back returns here)</option>
            <option value="drawer">Over this screen, in a drawer</option>
          </select>
        </>
      )}
      <label style={labelStyle}>Title (optional)</label>
      <input style={{ ...inputStyle, marginBottom: 6 }} value={b.title || ''} placeholder={def.name || 'Defaults to the button name'}
        onChange={e => patch({ title: e.target.value })} />
    </div>
  )
}
